/**
 * Tour 实现
 * */
import { createApp, ref } from 'vue'
import Tour from '../tour.vue'
import type { TourV2Step } from '../tour'
import type { TourCfg, TourReturn } from './types'

// 全局变量
const defaultTourConfig = {
  mask: true,
  type: 'default',
  visible: true,
  current: 1,
}

let _globalTourApp: ReturnType<typeof createApp> | null = null
let _globalTourVm: any

const destroy = (): void => {
  if (_globalTourApp) {
    _globalTourApp.unmount()
    _globalTourApp = null
    _globalTourVm = null
  }
}

const norlizeCurrent = (current: number, total: number): number => {
  if (!current) return 1
  const currentInteger = Number.parseInt(current.toString())
  if (!currentInteger) return 1
  return Math.max(1, Math.min(currentInteger, total))
}

const getConfigValue = (
  config: TourCfg | TourV2Step[],
  prop: keyof TourCfg,
  defaultValue?: any
): any => {
  return !Array.isArray(config) ? config[prop] ?? defaultValue : defaultValue
}

export function useTour(
  config: TourCfg | TourV2Step[],
  manually = false
): TourReturn {
  const current = ref(getConfigValue(config, 'current', 1))
  const total = Array.isArray(config)
    ? config.length
    : config.steps?.length ?? 0

  const show = (curr?: number): void => {
    if (_globalTourApp) {
      destroy()
    }

    current.value = curr || 1
    let tourProps = {} as any
    if (!Array.isArray(config)) {
      tourProps = {
        ...defaultTourConfig,
        ...config,
        current: current.value,
      }
    } else {
      tourProps = {
        ...defaultTourConfig,
        current: current.value,
        steps: config,
      }
    }
    tourProps.onChange = (curr: number): void => {
      current.value = curr
      if (!Array.isArray(config) && config.onChange) {
        config.onChange(curr)
      }
    }
    tourProps.onClose = (): void => {
      if (!Array.isArray(config) && config.onClose) {
        config.onClose()
      }
      destroy()
    }
    tourProps.onSkip = (): void => {
      if (!Array.isArray(config) && config.onSkip) {
        config.onSkip()
      }
      destroy()
    }
    tourProps.onFinish = (): void => {
      if (!Array.isArray(config) && config.onFinish) {
        config.onFinish()
      }
      destroy()
    }

    _globalTourApp = createApp(Tour, tourProps)
    _globalTourVm = _globalTourApp.mount(document.createElement('div'))
    step(current.value)
  }

  const step = (curr: number): void => {
    current.value = norlizeCurrent(curr, total)
    _globalTourVm?.step(current.value)
  }

  const hide = (): void => {
    destroy()
  }

  const prev = (): void => {
    if (_globalTourVm) {
      _globalTourVm?.prev()
      current.value = _globalTourVm.localCurrent
    }
  }

  const next = (): void => {
    if (_globalTourVm) {
      _globalTourVm?.next()
      current.value = _globalTourVm.localCurrent
    }
  }

  const update = (): void => {
    _globalTourVm?.update()
  }

  // auto show
  if (!manually && (Array.isArray(config) || !config.showManually)) {
    show(defaultTourConfig.current)
  }

  return {
    vm: _globalTourVm,
    current,
    show,
    step,
    hide,
    skip: hide,
    prev,
    next,
    update,
  }
}
